import type { ColumnDef } from "@tanstack/react-table";
import type { CellContext } from "@tanstack/react-table";
import { Check, CheckCircle2, Eye, Loader, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import { BankTxnStatus } from "@/apis/gql/types";
import { DateCell } from "@/components/shared/cells/date-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { RawTextCell } from "@/components/shared/cells/text";
import { SignedAmount } from "@/components/shared/miscellaneous/signed-amount";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { AutoBuchungTag } from "@/components/shared/tags/auto-buchung";
import { cn } from "@/lib/utils";

import { BankTransactionBankAccountIcon } from "@/components/shared/bank-transactions/bank-transaction-bank-account-icon";
import { AccountEntityType } from "@/lib/constants/account-types";

export const BankTxnStatusCell = ({
  row,
}: CellContext<Transaction, unknown>) => {
  const { t } = useTranslation();

  const transaction = row.original;

  const status = useMemo(() => {
    if (
      transaction.status === BankTxnStatus.REVIEWED ||
      transaction.status === BankTxnStatus.REVIEWED_ZERO_TOUCH
    ) {
      return {
        label: t("transactions.transaction_status.done"),
        color: "text-green-800",
        backgroundColor: "bg-green-100 border border-green-200",
        icon: Check,
      } as const;
    }

    if (transaction.status === BankTxnStatus.EXPORTED) {
      return {
        label: t("transactions.transaction_status.exported"),
        color: "text-indigo-800",
        backgroundColor: "bg-indigo-100 border border-indigo-200",
        icon: CheckCircle2,
      } as const;
    }

    if (transaction.status === BankTxnStatus.EXPORTING) {
      return {
        label: t("transactions.transaction_status.exporting"),
        color: "text-gray-800",
        backgroundColor: "bg-gray-100 border border-gray-200",
        icon: Loader,
      } as const;
    }

    if (transaction.status === BankTxnStatus.EXPORT_FAILED) {
      return {
        label: t("transactions.transaction_status.export_failed"),
        color: "text-red-800",
        backgroundColor: "bg-red-100 border border-red-200",
        icon: X,
      } as const;
    }

    return {
      label: t("transactions.transaction_status.to_review"),
      color: "text-orange-800",
      backgroundColor: "bg-orange-100 border border-orange-200",
      icon: Eye,
    } as const;
  }, [transaction.status, t]);

  return (
    <div
      className={cn(
        "flex border-0 items-center justify-start",
        row.getParentRow() && "opacity-40",
      )}
      style={{ paddingLeft: `${row.depth * 0}rem` }}
    >
      <div
        className={cn(
          "flex flex-row gap-1.5 items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis text-xs font-semibold py-0.5 px-2 rounded-md",
          status.backgroundColor,
          status.color,
        )}
      >
        {status.icon && <status.icon className="h-3.5 w-3.5" />}
        {status.label}
      </div>
    </div>
  );
};

const selectColumn: ColumnDef<Transaction> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-10 w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const createdAtColumn: ColumnDef<Transaction> = {
  id: "bookingDate",
  size: 90,
  accessorKey: "bookingDate",
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("date")} />;
  },
  cell: (cell) => {
    return (
      <DateCell value={cell.row.original.bookingDate} format="dd.MM.yyyy" />
    );
  },
};

const bankAccountColumn: ColumnDef<Transaction> = {
  accessorKey: "bankAccount.name",
  id: "bankAccountName",
  size: 220,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("bank-account-header")}
      />
    );
  },
  cell: (cell) => (
    <BankTransactionBankAccountIcon
      transaction={cell.row.original}
      showBankName={true}
    />
  ),
};

const amountColumn: ColumnDef<Transaction> = {
  accessorKey: "amount",
  id: "amount",
  size: 150,
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("amount")} />;
  },
  cell: (cell) => {
    const transaction = cell.row.original;

    return (
      <SignedAmount
        side={transaction.side}
        amount={transaction.amount}
        currency={transaction.currency}
        originalAmount={transaction.originalAmount}
        originalCurrency={transaction.originalCurrency}
      />
    );
  },
};

const descriptionColumn: ColumnDef<Transaction> = {
  id: "counterpartyName",
  accessorKey: "counterpartyName",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transactions.all.columns.partner")}
      />
    );
  },
  cell: (cell) => <RawTextCell value={cell.row.original.counterpartyName} />,
};

const purposeColumn: ColumnDef<Transaction> = {
  id: "purpose",
  accessorKey: "purpose",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transactions.all.columns.purpose")}
      />
    );
  },
  cell: (cell) => <RawTextCell value={cell.row.original.purpose} />,
};

const statusColumn: ColumnDef<Transaction> = {
  id: "smbAttachmentStatus",
  accessorKey: "smbAttachmentStatus",
  size: 160,
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("status")} />;
  },
  cell: (cell) => <BankTxnStatusCell {...cell} />,
};

const autoBookingColumn: ColumnDef<Transaction> = {
  id: "autoBooking",
  accessorKey: "smbAttachmentStatus",
  size: 150,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("auto_booking")} />
    );
  },
  cell: ({ row }: CellContext<Transaction, unknown>) => {
    const transaction = row.original;

    const zeroTouch = transaction.status === BankTxnStatus.REVIEWED_ZERO_TOUCH;

    if (zeroTouch) {
      return (
        <div
          className={cn(
            "flex border-0 items-center justify-start",
            row.getParentRow() && "opacity-40",
          )}
          style={{ paddingLeft: `${row.depth * 0}rem` }}
        >
          <AutoBuchungTag />
        </div>
      );
    }
    return null;
  },
};

const accountColumn: ColumnDef<Transaction> = {
  id: "account",
  accessorKey: "account",
  size: 250,
  header: (header) => {
    const { t } = useTranslation();
    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("counter_account")}
      />
    );
  },
  cell: (cell) => {
    const transaction = cell.row.original;

    const counterAccounts = transaction.journalEntries
      .flatMap((entry) => entry.journalLines)
      .map((line) => line.journalAccount)
      .filter(
        (account) => account.entityType !== AccountEntityType.BANK_ACCOUNT,
      )
      .sort((a, b) => {
        if (a.entityType === AccountEntityType.COUNTERPARTY) return -1;
        if (b.entityType === AccountEntityType.COUNTERPARTY) return 1;
        return 0;
      });

    return (
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 px-1">
        <div className="min-w-0">
          <RawTextCell
            className="text-left"
            value={counterAccounts.map((account) => account.name).join(", ")}
          />
        </div>
        <div className="text-gray-500 text-xs min-w-0">
          <RawTextCell
            value={counterAccounts.map((account) => account.number).join(", ")}
          />
        </div>
      </div>
    );
  },
};

const buColumn: ColumnDef<Transaction> = {
  id: "bu",
  accessorKey: "bu",
  size: 100,
  header: (header) => {
    const { t } = useTranslation();
    return <DataTableColumnHeader column={header.column} title="BU" />;
  },
  cell: (cell) => {
    const transaction = cell.row.original;

    const taxCodes = transaction.journalEntries
      .flatMap((entry) => entry.journalLines)
      .map((line) => line.taxCode)
      .filter(
        (taxCode): taxCode is NonNullable<typeof taxCode> => taxCode !== null,
      )
      .filter(
        (taxCode, index, self) =>
          self.findIndex((t) => t.datevBu === taxCode.datevBu) === index,
      );

    if (taxCodes.length === 0) {
      return <p className="text-gray-400 p-1 text-center">—</p>;
    }

    return (
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="min-w-0">
          <RawTextCell
            className="font-mono text-xs text-center"
            value={taxCodes.map((taxCode) => taxCode.datevBu).join(", ")}
          />
        </div>
      </div>
    );
  },
};

const documentNumberColumn: ColumnDef<Transaction> = {
  id: "documentNumber",
  accessorKey: "documentNumber",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Belegnr." />
  ),
  cell: (cell) => {
    const transaction = cell.row.original;

    const entries = transaction.journalEntries.filter(
      (entry) => entry.documentNumber,
    );

    const value = useMemo(() => {
      if (entries.length === 0) {
        return "—";
      }

      return entries.map((entry) => entry.documentNumber).join(", ");
    }, [entries]);

    return (
      <div className="p-1">
        <RawTextCell
          value={value}
          className={cn(entries.length > 0 ? "" : "text-gray-400")}
        />
      </div>
    );
  },
};

const bookingTextColumn: ColumnDef<Transaction> = {
  id: "bookingText",
  accessorKey: "bookingText",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Buchungstext" />
  ),
  cell: (cell) => {
    const transaction = cell.row.original;

    const entries = transaction.journalEntries.filter(
      (entry) => entry.bookingText,
    );

    const value = useMemo(() => {
      if (entries.length === 0) {
        return "—";
      }

      return entries.map((entry) => entry.bookingText).join(", ");
    }, [entries]);

    return (
      <div className="p-1">
        <RawTextCell
          value={value}
          className={cn(entries.length > 0 ? "" : "text-gray-400")}
        />
      </div>
    );
  },
};

export const columns: ColumnDef<Transaction>[] = [
  selectColumn,
  statusColumn,
  createdAtColumn,
  bankAccountColumn,
  descriptionColumn,
  purposeColumn,
  amountColumn,
  accountColumn,
  buColumn,
  documentNumberColumn,
  bookingTextColumn,
  autoBookingColumn,
];
